import type { ReactElement } from 'react';
import React from 'react';
import { Stage } from 'corgi-types';

import { Locale } from '../enums/Locale';
import { useLocalization } from '../providers/LocaleProvider';

interface GlobalGTMScriptConfig {
  id: string;
  gtmAuth: string;
  gtmPreview: string;
  locale?: Locale;
}

/**
 * Generic Webshop Tracking Overview:
 * https://home24.atlassian.net/wiki/spaces/TT/pages/174817475/Generic+Webshop+Tracking+Overview
 */

const testIds = {
  DE: 'GTM-PJCRH2',
  AT: 'GTM-WM8NXQ',
  CH: 'GTM-TLJ9TR',
  BE: 'GTM-5RZM5W',
  FR: 'GTM-K3HNX8',
  IT: 'GTM-PJS2JL',
  NL: 'GTM-M8KBBT',
};

export const GTM_ID = {
  GLOBAL: 'GTM-KXZ76W',
  [Stage.Production]: {
    DE: 'GTM-SDW3',
    AT: 'GTM-JF6J',
    CH: 'GTM-TV2ZBF',
    BE: 'GTM-NJD3K6',
    FR: 'GTM-FDFSX',
    IT: 'GTM-PNRS2P',
    NL: 'GTM-JRBHD',
  },
  [Stage.Testing]: testIds,
  [Stage.Staging]: testIds,
  [Stage.Development]: testIds,
};

export const GTM_PREVIEW = {
  [Stage.Production]: 'env-1',
  [Stage.Staging]: 'env-6',
  [Stage.Testing]: 'env-5',
  [Stage.Development]: 'env-4',
};

export const GTM_AUTH = {
  [Stage.Production]: 'ti5q6VPellAz4UX8DKzIKw',
  [Stage.Testing]: 'eJ4xn0EIMMgXkbDFe6AYow',
  [Stage.Staging]: '5KZeUSqVOvK7C9-aUH40ug',
  [Stage.Development]: 'eJ4xn0EIMMgXkbDFe6AYow',
};

const GLOBAL_GTM_URL = {
  [Locale.De_De_H24]: 'https://gtms.home24.de',
  [Locale.De_At]: 'https://gtms.home24.at',
  [Locale.De_Ch]: 'https://gtms.home24.ch',
  [Locale.Fr_Be]: 'https://gtms.home24.be',
  [Locale.Fr_Ch]: 'https://gtms.home24.ch',
  [Locale.Fr_Fr]: 'https://gtms.home24.fr',
  [Locale.It_It]: 'https://gtms.home24.it',
  [Locale.Nl_Be]: 'https://gtms.home24.be',
  [Locale.Nl_Nl]: 'https://gtms.home24.nl',
};

export const getGlobalGtmScriptSrc = ({ id, gtmAuth, gtmPreview, locale }: GlobalGTMScriptConfig) =>
  `${GLOBAL_GTM_URL[locale]}/gtm.js?id=${id}${
    gtmAuth && gtmPreview
      ? `&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x`
      : gtmAuth
        ? `&gtm_auth=${gtmAuth}&gtm_cookies_win=x`
        : ''
  }`;

export const getRegionGtmScriptSrc = (id: string) =>
  `https://www.googletagmanager.com/gtm.js?id=${id}`;

/**
 * GTM - NoScript
 */
export const NoScriptSnippet = ({ title, src }) => (
  <noscript data-testid={title} data-test-title={title} data-test-src={src}>
    <iframe
      title={title}
      src={src}
      height="0"
      width="0"
      style={{ display: 'none', visibility: 'hidden' }}
    />
  </noscript>
);

export const getGlobalGtmNoScript = ({ id, gtmAuth, gtmPreview }: GlobalGTMScriptConfig) => (
  <NoScriptSnippet
    title="global-gtm-noscript"
    src={`https://www.googletagmanager.com/ns.html?id=${id}${
      gtmAuth && gtmPreview
        ? `&gtm_auth=${gtmAuth}&gtm_preview=${gtmPreview}&gtm_cookies_win=x`
        : ''
    }`}
  />
);

export const getRegionGtmNoScript = (id: string) => (
  <NoScriptSnippet
    title="region-gtm-noscript"
    src={`https://www.googletagmanager.com/ns.html?id=${id}`}
  />
);

interface GTMRenderConfig {
  src: {
    global: string;
    region: string;
  };
  noscript: {
    global: ReactElement<any>;
    region: ReactElement<any>;
  };
}

export const useGTMConfig = (env: Stage): GTMRenderConfig => {
  const { region, locale } = useLocalization();
  const globalId = GTM_ID.GLOBAL;
  const regionId = GTM_ID[env][region];
  const gtmAuth = GTM_AUTH[env];
  const gtmPreview = GTM_PREVIEW[env];

  return {
    src: {
      global: getGlobalGtmScriptSrc({
        id: globalId,
        gtmAuth,
        gtmPreview,
        locale,
      }),
      region: getRegionGtmScriptSrc(regionId),
    },
    noscript: {
      global: getGlobalGtmNoScript({ id: globalId, gtmAuth, gtmPreview }),
      region: getRegionGtmNoScript(regionId),
    },
  };
};
